/** Returns the modulo of a / b, depending on the sign of b.
 *
 * @param {number} a Dividend.
 * @param {number} b Divisor.
 * @return {number} Modulo.
 */

const modulo = (a: number, b: number) => {
  const r = a % b;
  return r * b < 0 ? r + b : r;
};

/**
 * Returns a number with a limited number of decimal digits.
 * @param {number} n The input number.
 * @param {number} decimals The maximum number of decimal digits.
 * @return {number} The input number with a limited number of decimal digits.
 */

const toFixed = (n: number, decimals: number) => {
  const factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
};

/**
 * @param {number} formattedNumber Number to be formatted
 * @param {number} width The desired width
 * @param {number} [precision] Precision of the output string (i.e. number of decimal places)
 * @return {string} Formatted string
 */

const padNumber = (formattedNumber: number, width: number, precision?: number) => {
  const numberString = precision !== undefined ? formattedNumber.toFixed(precision) : '' + formattedNumber;
  let decimal = numberString.indexOf('.');
  decimal = decimal === -1 ? numberString.length : decimal;
  return decimal > width ? numberString : new Array(1 + width - decimal).join('0') + numberString;
};

const FormatDecimals = {
  DMS: {
    d: 0,
    m: 0,
    s: 2,
  },
  DD: {
    d: 5,
    m: 0,
    s: 0,
  },
  DMM: {
    d: 0,
    m: 4,
    s: 0,
  },
};

/**
 * @param {string} hemispheres Hemispheres.
 * @param {number} degrees Degrees.
 * @param {number} [fractionDigits] The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {string} String.
 */

export const getHDMS = (hemispheres: string, degrees: number, format: keyof typeof FormatDecimals) => {
  const normalizedDegrees = modulo(degrees + 180, 360) - 180;
  const x = Math.abs(3600 * normalizedDegrees);
  const fractionDigits = FormatDecimals[format];

  let deg = toFixed(x / 3600, fractionDigits.d);
  let min = toFixed((x - deg * 3600) / 60, fractionDigits.m);
  let sec = toFixed(x - deg * 3600 - min * 60, fractionDigits.s);

  if (sec >= 60) {
    sec = 0;
    min += 1;
  }

  if (min >= 60) {
    min = 0;
    deg += 1;
  }
  if (min < 0) {
    min = 60 + min;
  }
  if (sec < 0) {
    sec = 60 + sec;
  }

  let hdms = deg + '\u00b0';

  if (min !== 0 || sec !== 0) {
    hdms += ' ' + padNumber(min, 2, fractionDigits.m) + '\u2032';
  }
  if (sec !== 0) {
    hdms += ' ' + padNumber(sec, 2, fractionDigits.s) + '\u2033';
  }

  if (normalizedDegrees !== 0) {
    hdms += ' ' + hemispheres.charAt(normalizedDegrees < 0 ? 1 : 0);
  }

  return hdms;
};
/**
 * Format a geographic coordinate with the hemisphere, degrees, minutes, and
 * seconds.
 *
 * @param {[[number, number]]} coordinate Coordinate array.
 * @param {number} [fractionDigits] The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {string} Hemisphere, degrees, minutes and seconds.
 * @api
 */
export const glToStringHDMS = (coordinate: number[], format: keyof typeof FormatDecimals) => {
  if (coordinate) {
    return getHDMS('NS', coordinate[1], format) + ' ' + getHDMS('EW', coordinate[0], format);
  }
  return '';
};
